import { KalturaAsset, KalturaMediaImage } from '@kaltura-ott/ott-client-types/api/types';

import { AssetPresentationRatioEnum, KuxRailImageArg, KuxProgram } from '../../interfaces';
import { getRatioByType } from '../image-types';

import { KalturaCategoryMetas, normalizeCategoryMetas } from './meta';

export let imageScale = 2;

export const setImageScale = (newImageScale: number) => {
  imageScale = newImageScale;
};

interface KuxImageRail {
  height: number;
  width: number;
  originImageUrl?: string;
  imageUrl?: string;
}

/**
 * Get resize url
 * @param url: string
 * @param height: number
 * @param width: number
 * @param quality: number
 */
export const getResizerUrl = (url: string, height?: number, width?: number, quality?: number): string => {
  let resizedUrl = url;

  if (height) {
    resizedUrl = `${resizedUrl}/height/${Math.ceil(height)}`;
  }
  if (width) {
    resizedUrl = `${resizedUrl}/width/${Math.ceil(width)}`;
  }

  if (quality) {
    resizedUrl = `${resizedUrl}/quality/${Math.ceil(quality)}`;
  }

  return resizedUrl;
};

/**
 * Calculate width by height
 * @param height
 * @param ratio
 */
export const getWidthByHeight = (height?: number, ratio?: AssetPresentationRatioEnum | string): number => {
  if (!ratio || !height) {
    return 0;
  }

  const [widthRatio, heightRatio] = ratio.split(/:|x/).map(Number);
  return Math.ceil((height / heightRatio) * widthRatio);
};

/**
 * Calculate height by width
 * @param width
 * @param ratio
 */
export const getHeightByWidth = (width: number, ratio: AssetPresentationRatioEnum): number => {
  const [widthRatio, heightRatio] = ratio.split(/:|x/).map(Number);
  return Math.ceil((width / widthRatio) * heightRatio);
};

export const findByRatio = (asset: KalturaAsset, ratio: AssetPresentationRatioEnum): KalturaMediaImage | undefined =>
  asset.images.find((image) => (image.ratio as unknown as AssetPresentationRatioEnum) === ratio);

export const findByType = (asset: KalturaAsset, type?: number): KalturaMediaImage | undefined => {
  return asset.images.find((image) => image.imageTypeId === type);
};

/**
 *
 * @param options: KuxRailImageArg
 * @param ratio: AssetPresentationRatioEnum
 */
export const calculateSizePerDevice = (options: KuxRailImageArg, ratio: AssetPresentationRatioEnum | string) => {
  const height = Math.ceil(options.assetImageHeight * options.sizeIndex);
  const width = Math.ceil(getWidthByHeight(height, ratio));
  return { width, height };
};

/**
 * Find ratio
 * DEPRECATED
 *
 * @param asset
 * @param options
 */
export const getRatioByAsset = (
  asset: KalturaAsset | KuxProgram,
  options: KuxRailImageArg,
): AssetPresentationRatioEnum => {
  if (!options.assetImageSource) {
    return options.assetPresentationRatio || AssetPresentationRatioEnum['1x1'];
  }

  let metas = asset.metas;
  if (asset instanceof KalturaAsset) {
    metas = normalizeCategoryMetas(asset.metas as KalturaCategoryMetas);
  }
  const defaultRatio = metas['default_ratio'] as unknown as AssetPresentationRatioEnum;
  return defaultRatio || options.assetPresentationRatio;
};

export const getTypeByAsset = (asset: KalturaAsset | KuxProgram, options: KuxRailImageArg): number | undefined => {
  if (!options.assetImageSource) {
    return options.assetPresentationImageType;
  }

  let metas = asset.metas;
  if (asset instanceof KalturaAsset) {
    metas = normalizeCategoryMetas(asset.metas as KalturaCategoryMetas);
  }

  const defaultType = metas['asset_presentation_image_type'] as unknown as number;
  return defaultType || options.assetPresentationImageType;
};

/**
 * Find image for rail item
 * DEPRECATED
 *
 * @param asset
 * @param options
 */
export const findRailImage = (asset: KalturaAsset | KuxProgram, options: KuxRailImageArg): KuxImageRail => {
  // TODO REMOVE TEMP BE issue
  if (!options.assetPresentationRatio) {
    return {
      height: 0,
      width: 0,
    };
  }

  const ratio = getRatioByAsset(asset, options);

  const { width, height } = calculateSizePerDevice(options, ratio);
  let originImage;
  if (asset instanceof KalturaAsset) {
    originImage = findByRatio(asset, ratio);
  } else {
    // KuxProgram case
    originImage = asset.images && asset.images[ratio];
  }

  if (originImage) {
    return {
      height,
      width,
      originImageUrl: originImage.url,
      imageUrl: getResizerUrl(originImage.url, height * imageScale, width * imageScale),
    };
  }
  return {
    height,
    width,
  };
};

/**
 * DEPRECATED
 */
export const findRailImageByType = (asset: KalturaAsset | KuxProgram, options: KuxRailImageArg): KuxImageRail => {
  const type = getTypeByAsset(asset, options);
  const ratio = getRatioByType(options.assetPresentationImageType);

  let originImage: KalturaMediaImage | KuxImage | undefined;
  if (asset instanceof KalturaAsset) {
    if (type) {
      originImage = findByType(asset, type);
    }
  } else {
    if (asset.images && type) {
      originImage = asset.images[type];
    }
  }

  if (originImage) {
    const { width, height } = calculateSizePerDevice(options, originImage.ratio);

    return {
      height,
      width,
      originImageUrl: originImage.url,
      imageUrl: getResizerUrl(originImage.url, height * imageScale, width * imageScale),
    };
  }

  const size = ratio && calculateSizePerDevice(options, ratio);
  return size ?? { height: 0, width: 0 };
};

export type KuxImage = {
  url: string;
  ratio: AssetPresentationRatioEnum;
};

export type KuxImages = {
  [key in AssetPresentationRatioEnum | number]?: KuxImage;
};

export const toKuxImages = (images: KalturaMediaImage[]): KuxImages => {
  const result = {};

  for (const image of images) {
    const imageData = { url: image.url, ratio: image.ratio };

    // @ts-expect-error TODO Fix
    result[image.ratio] = imageData;
    // @ts-expect-error TODO Fix
    result[image.imageTypeId] = imageData;
  }

  return result;
};

export const toKuxImagesFromCategory = (images: KalturaMediaImage[]): KuxImages => {
  const result: { [ratio in AssetPresentationRatioEnum]?: { url: string; ratio: AssetPresentationRatioEnum } } = {};

  for (const image of images) {
    const ratio = (image.ratio || getRatioByType(image.imageTypeId)) as AssetPresentationRatioEnum;

    if (ratio) {
      result[ratio] = { url: image.url, ratio };
    }
  }

  return result;
};
